// Core financial math and formatting helpers

export const uid = () => Math.random().toString(36).substring(2, 9).toUpperCase();

export type Numeric = string | number | null | undefined;

export const N = (v: Numeric): number => {
  const num = parseFloat(String(v ?? 0));
  return isNaN(num) ? 0 : num;
};

export interface Deduction { tp: string; val: Numeric; position?: 'before_vat' | 'after_vat' }
export interface CnItem { ref: string; amt: number; taxPct?: number }
export interface RetItem { type: string; pct: number }

export const calcDeds = (deds: Deduction[] = [], gross: Numeric) => {
  return deds.reduce((s, d) => s + (d.tp === 'pct' ? N(gross) * N(d.val) / 100 : N(d.val)), 0);
};

export const calcDedsBeforeVat = (deds: Deduction[] = [], gross: Numeric) => {
  return deds
    .filter(d => (d.position ?? 'after_vat') === 'before_vat')
    .reduce((s, d) => s + (d.tp === 'pct' ? N(gross) * N(d.val) / 100 : N(d.val)), 0);
};

export const calcDedsAfterVat = (deds: Deduction[] = [], gross: Numeric) => {
  return deds
    .filter(d => (d.position ?? 'after_vat') === 'after_vat')
    .reduce((s, d) => s + (d.tp === 'pct' ? N(gross) * N(d.val) / 100 : N(d.val)), 0);
};

export const sumCnItems = (cnItems: CnItem[] | undefined | null, legacy: Numeric = 0) => {
  if (cnItems && cnItems.length > 0) {
    return cnItems.reduce((s, c) => s + N(c.amt) + N(c.amt) * N(c.taxPct ?? 0), 0);
  }
  return N(legacy);
};

export const sumRetItems = (retItems: RetItem[] | undefined | null, gross: Numeric, legacyPct: Numeric = 0): number => {
  if (retItems && retItems.length > 0) {
    return retItems.reduce((s, r) => s + N(gross) * N(r.pct), 0);
  }
  return N(gross) * N(legacyPct);
};

export const sumRef = (arr: { invRef?: string | null; amount: Numeric }[] = [], ref: string) => {
  return arr.filter(r => r.invRef === ref).reduce((s, r) => s + N(r.amount), 0);
};

const ADVANCE_RECEIPT_TYPES = ['Advance mobilisation', 'Advance'];
const RETENTION_RELEASE_TYPES = ['Retention Release'];

interface InvWithRet { gross: Numeric; retPct?: Numeric; advRecPct?: Numeric; cnAmt?: Numeric; cnItems?: CnItem[]; retItems?: RetItem[]; deds?: Deduction[] }
export const calcCI = (inv: InvWithRet, recs: { invRef?: string | null; amount: Numeric; tp?: string }[], vatRate: Numeric = 0) => {
  const gr  = N(inv.gross);
  const dedsBefore = calcDedsBeforeVat(inv.deds, gr);
  const dedsAfter  = calcDedsAfterVat(inv.deds, gr);
  const adv = gr * N(inv.advRecPct);
  const vatBase = gr - dedsBefore - adv;
  const va  = vatBase * N(vatRate);
  const tot = vatBase + va;
  const ret = sumRetItems(inv.retItems, gr, inv.retPct);
  const cn  = sumCnItems(inv.cnItems, inv.cnAmt);
  const net = (vatBase + va) - ret - dedsAfter - cn;
  const progressRecs = recs.filter(r => !ADVANCE_RECEIPT_TYPES.includes(r.tp ?? "") && !RETENTION_RELEASE_TYPES.includes(r.tp ?? ""));
  const coll = sumRef(progressRecs, (inv as { id?: string }).id ?? "");
  const ded = dedsBefore + dedsAfter;
  return { va, tot, ret, adv, ded, net, coll, bal: net - coll, dedsBefore, dedsAfter, cn };
};

interface ScInvWithRet { gross: Numeric; retPct?: Numeric; advRecPct?: Numeric; cnAmt?: Numeric; cnItems?: CnItem[]; retItems?: RetItem[]; deds?: Deduction[] }
export const calcSCI = (inv: ScInvWithRet, pmts: { invRef?: string | null; amount: Numeric; tp?: string }[], vatRate: Numeric = 0) => {
  const gr = N(inv.gross);
  const dedsBefore = calcDedsBeforeVat(inv.deds, gr);
  const dedsAfter  = calcDedsAfterVat(inv.deds, gr);
  const base = gr - dedsBefore;
  const ret = sumRetItems(inv.retItems, base, inv.retPct);
  const adv = base * N(inv.advRecPct);
  const cn  = sumCnItems(inv.cnItems, inv.cnAmt);
  const net = base - ret - adv - dedsAfter - cn;
  const vat = net * N(vatRate);
  const totalPayable = net + vat;
  const invoicePmts = pmts.filter(p => p.tp !== 'Advance');
  const paid = sumRef(invoicePmts, (inv as { id?: string }).id ?? "");
  const ded = dedsBefore + dedsAfter;
  return { ret, adv, ded, net, vat, totalPayable, paid, bal: net - paid, dedsBefore, dedsAfter, cn };
};

interface SupplierInv { gross: Numeric; cnAmt?: Numeric; cnItems?: CnItem[]; deds?: Deduction[] }
export const calcSI = (inv: SupplierInv, pmts: { invRef?: string | null; amount: Numeric }[], vat: Numeric = 0.15) => {
  const gr = N(inv.gross);
  const dedsBefore = calcDedsBeforeVat(inv.deds, gr);
  const dedsAfter  = calcDedsAfterVat(inv.deds, gr);
  const vatBase = gr - dedsBefore;
  const va = vatBase * N(vat);
  const tot = vatBase + va;
  const cn  = sumCnItems(inv.cnItems, inv.cnAmt);
  const net = tot - dedsAfter - cn;
  const paid = sumRef(pmts, (inv as { id?: string }).id ?? "");
  const ded = dedsBefore + dedsAfter;
  return { va, tot, ded, net, paid, bal: net - paid, dedsBefore, dedsAfter, cn };
};

export const stBadge = (bal: number, paid: number) => {
  if (bal <= 0) return { lb: 'Paid',    cl: 'text-emerald-700', bg: 'bg-emerald-100 border-emerald-200' };
  if (paid > 0) return { lb: 'Partial', cl: 'text-amber-700',   bg: 'bg-amber-100 border-amber-200'   };
  return              { lb: 'Unpaid',  cl: 'text-rose-700',    bg: 'bg-rose-100 border-rose-200'     };
};

interface CurrencyConfig { sym: string; decimals: number }
const CURRENCIES: Record<string, CurrencyConfig> = {
  SAR: { sym: 'SAR', decimals: 0 },
  AED: { sym: 'AED', decimals: 0 },
  USD: { sym: 'USD', decimals: 0 },
  EUR: { sym: 'EUR', decimals: 0 },
  GBP: { sym: 'GBP', decimals: 0 },
  KWD: { sym: 'KWD', decimals: 3 },
  QAR: { sym: 'QAR', decimals: 0 },
  BHD: { sym: 'BHD', decimals: 3 },
  OMR: { sym: 'OMR', decimals: 3 },
  EGP: { sym: 'EGP', decimals: 0 },
  JOD: { sym: 'JOD', decimals: 3 },
};

export const fc = (v: Numeric, cur = 'SAR') => {
  const c = CURRENCIES[cur] ?? CURRENCIES.SAR;
  const dec = c.decimals;
  const num = dec === 0 ? Math.round(N(v)) : +(N(v).toFixed(dec));
  return `${c.sym} ${new Intl.NumberFormat('en', { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(num)}`;
};

export const fp = (v: Numeric) => `${(N(v) * 100).toFixed(1)}%`;

export const separateAdvances = (receipts: { tp?: string | null; amount: Numeric }[]) => {
  const progressReceipts = receipts.filter(r => !ADVANCE_RECEIPT_TYPES.includes(r.tp ?? '') && !RETENTION_RELEASE_TYPES.includes(r.tp ?? ''));
  const advanceReceipts = receipts.filter(r => ADVANCE_RECEIPT_TYPES.includes(r.tp ?? ''));
  const retentionReleaseReceipts = receipts.filter(r => RETENTION_RELEASE_TYPES.includes(r.tp ?? ''));
  return {
    progressTotal: progressReceipts.reduce((s, r) => s + N(r.amount), 0),
    advanceTotal: advanceReceipts.reduce((s, r) => s + N(r.amount), 0),
    retentionReleaseTotal: retentionReleaseReceipts.reduce((s, r) => s + N(r.amount), 0),
    progressReceipts,
    advanceReceipts,
    retentionReleaseReceipts,
  };
};

export const calcAdvanceRecovery = (scInvoices: { gross: Numeric; advRecPct?: Numeric }[]) => {
  return scInvoices.reduce((s, inv) => s + N(inv.gross) * N(inv.advRecPct), 0);
};

export const calcClientAdvanceRecovery = (
  advanceTotal: number,
  ctrVal: Numeric,
  grossBilled: number,
): number => {
  const ctr = N(ctrVal);
  if (ctr <= 0 || advanceTotal <= 0) return 0;
  const recoveryRate = Math.min(1, advanceTotal / ctr);
  return Math.min(advanceTotal, grossBilled * recoveryRate);
};
